// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, ChangeDetectionStrategy, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AvatarComponent } from '@lfx/shared/components/avatar/avatar.component';

/**
 * Info interface for avatar with name display
 * Contains only the properties needed for rendering
 */
export interface Info {
  slug: string;
  logoUrl: string;
  name: string;
}

/**
 * Avatar with Name Column Component
 * 
 * Generic, reusable component for displaying an avatar with a name.
 * Can be used in any table or list that needs this presentation pattern.
 * Supports both clickable (with routing) and non-clickable display modes.
 */
@Component({
  selector: 'lfx-avatar-with-name-column',
  imports: [CommonModule, RouterModule, AvatarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isClickable()) {
      <a class="info-container clickable"
         [routerLink]="[info().slug]">
        <lfx-avatar
          [imageURL]="info().logoUrl"
          [name]="info().name"
          type="project"
          size="md"
          [isObjectCover]="false">
        </lfx-avatar>
        <div class="details">
          <span class="name lf-body-bold-sm">{{ info().name }}</span>
        </div>
      </a>
    } @else {
      <div class="info-container">
        <lfx-avatar
          [imageURL]="info().logoUrl"
          [name]="info().name"
          type="project"
          size="md"
          [isObjectCover]="false">
        </lfx-avatar>
        <div class="details">
          <span class="name lf-body-bold-sm">{{ info().name }}</span>
        </div>
      </div>
    }
  `,
  styleUrls: ['./avatar-with-name-column.component.scss']
})
export class AvatarWithNameColumnComponent {
  readonly info = input.required<Info>();
  readonly isClickable = input<boolean>(true);
}

